import {useContext, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {useUpsertRegistryProjectMutation} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {
  doNotRetryContext,
  propagateErrorsContext,
} from '../../../../util/errors';
import {RegistryAccessOptionType} from '../../common/permissionUtils';
import {RegistriesSearchContext} from '../../common/RegistriesSearchContextProvider';
import {
  checkRegistryProject,
  DEFAULT_REGISTRY_PROJECT_TYPES,
} from '../../common/utils';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../../graphql/registryProjects.query';
import {FETCH_REGISTRIES_PAGE_SIZE} from '../../RegistriesHomepage/hooks';

export type UpsertAction = 'Create' | 'Edit';

type UpsertRegistryProjectInputType = {
  upsertAction: UpsertAction;
  description: string;
  registryProjectName: string;
  entityName: string;
  access: RegistryAccessOptionType;
  inputArtifactTypes: string[];
  allowAllArtifactTypesInRegistry?: boolean;
  ownerId?: string;
};

export function useUpsertRegistryProjectWithTypes(
  orgName: string,
  showToast = true
) {
  const {searchPattern} = useContext(RegistriesSearchContext);
  const [upsertRegistryProjectMutation] = useUpsertRegistryProjectMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_REGISTRY_PROJECTS_PARTIAL,
        variables: {
          orgName,
          pattern: searchPattern,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
        },
      },
      {
        query: FETCH_REGISTRY_PROJECTS_PARTIAL,
        variables: {
          orgName,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
        },
      },
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  async function upsertRegistryProjectWithTypes(
    input: UpsertRegistryProjectInputType
  ) {
    const {
      upsertAction,
      description,
      registryProjectName,
      entityName,
      access,
      inputArtifactTypes,
      allowAllArtifactTypesInRegistry,
      ownerId,
    } = input;
    const isEditMode = upsertAction === 'Edit';
    let upsertRegistryProject;
    setIsLoading(true);
    try {
      if (
        entityName !== '' &&
        registryProjectName !== '' &&
        allowAllArtifactTypesInRegistry != null
      ) {
        let artifactTypes = undefined;
        if (inputArtifactTypes.length > 0) {
          artifactTypes = inputArtifactTypes.map((artifactType: string) => ({
            name: artifactType,
          }));
        }
        const trimmedRegistryProjectName = registryProjectName.trim();
        upsertRegistryProject = await upsertRegistryProjectMutation({
          variables: {
            description,
            name: trimmedRegistryProjectName,
            entityName,
            access,
            allowAllArtifactTypesInRegistry,
            owner: ownerId,
            artifactTypes,
          },
        });
        const {registryName} = checkRegistryProject(trimmedRegistryProjectName);
        if (
          registryName != null &&
          DEFAULT_REGISTRY_PROJECT_TYPES.includes(registryName)
        ) {
          Analytics.track('Registry interaction', {
            action: 'create custom registry',
            entityName,
            registryProjectName,
            registryName,
          });
        }
      }
      if (showToast) {
        toast(isEditMode ? 'Updated registry settings!' : 'Created registry!');
      }
      setIsLoading(false);
      return upsertRegistryProject;
    } catch (error) {
      toast(
        `Something went wrong while trying to ${
          isEditMode ? 'update' : 'create'
        } the registry`
      );
      setIsLoading(false);
      return undefined;
    }
  }
  return {upsertRegistryProjectWithTypes, isLoading};
}
